import { chatThreadsService } from "@/api/chat/chatThreads.service";
import type { TChatThreadHistoryMessage } from "@/api/chat/chatThreads.types";
import { investorChatService, type TInvestorChatServerMessage } from "@/api/dealNetwork/investorChat.service";
import type { TMessage } from "@/types/chat";
import { extractUsageSummary, type TAIUsageSummary } from "@/utils/aiUsage";
import { tokenStorage } from "@/utils/tokenStorage";
import {
  handleWebSocketAuthError,
  handleWebSocketCloseAuthError,
  mapWebSocketCloseCodeToMessage,
} from "@/utils/websocketUtils";
import { useCallback, useEffect, useRef, useState } from "react";

type TInvestorChatStatus = "idle" | "connecting" | "connected" | "error";

type TUseInvestorChatParams = {
  investorCompanyId: string | null;
  founderCompanyId: string;
  threadId: string | null;
  enabled: boolean;
  initialPrompt?: string | null;
};

type TUseInvestorChatResult = {
  messages: TMessage[];
  status: TInvestorChatStatus;
  error: string | null;
  isThinking: boolean;
  latestUsage: TAIUsageSummary | null;
  sendMessage: (content: string) => void;
};

const PING_INTERVAL_MS = 30_000;
const consumedInitialPromptThreadIds = new Set<string>();

export function useInvestorChat({
  investorCompanyId,
  founderCompanyId,
  threadId,
  enabled,
  initialPrompt,
}: TUseInvestorChatParams): TUseInvestorChatResult {
  const [messages, setMessages] = useState<TMessage[]>([]);
  const [status, setStatus] = useState<TInvestorChatStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [isThinking, setIsThinking] = useState(false);
  const [latestUsage, setLatestUsage] = useState<TAIUsageSummary | null>(null);

  const websocketRef = useRef<WebSocket | null>(null);
  const threadIdRef = useRef<string | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pendingMessageRef = useRef<string | null>(null);
  const unmountingRef = useRef(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setErrorState = useCallback((msg: string) => {
    setError(msg);
    setStatus("error");
  }, []);

  const clearPingInterval = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
  }, []);

  const connect = useCallback(() => {
    if (!investorCompanyId || !enabled || !threadId) return;
    if (websocketRef.current?.readyState === WebSocket.OPEN) return;
    if (websocketRef.current?.readyState === WebSocket.CONNECTING) return;

    const token = tokenStorage.getAccessToken();
    if (!token) {
      setErrorState("Not authenticated");
      return;
    }

    threadIdRef.current = threadId;
    setStatus("connecting");
    setError(null);

    const url = investorChatService.getWebSocketURL(threadId, token, investorCompanyId);
    if (import.meta.env.DEV) {
      console.info("[investor-chat] Opening WebSocket connection", {
        url: url.replace(/token=[^&]+/, "token=***"),
        threadId,
        investorCompanyId,
      });
    }
    const ws = new WebSocket(url);
    websocketRef.current = ws;

    ws.onopen = () => {
      if (unmountingRef.current || websocketRef.current === null) return;
      if (import.meta.env.DEV) {
        console.info("[investor-chat] WebSocket connected");
      }
      setStatus("connected");
      setError(null);
      const pending = pendingMessageRef.current;
      if (pending) {
        pendingMessageRef.current = null;
        if (import.meta.env.DEV) {
          console.info("[investor-chat] Sending initial prompt", {
            threadId,
            messageLength: pending.length,
          });
        }
        ws.send(
          JSON.stringify({
            type: "investor_chat_message",
            section: "deal_pipeline",
            content: pending,
            company_id: founderCompanyId,
          })
        );
        setMessages((prev) => [
          ...prev,
          { id: `optimistic-${Date.now()}`, text: pending, sender: "user", optimistic: true },
        ]);
        setIsThinking(true);
      }
      pingIntervalRef.current = setInterval(() => {
        if (websocketRef.current?.readyState === WebSocket.OPEN) {
          websocketRef.current.send(JSON.stringify({ type: "ping" }));
        }
      }, PING_INTERVAL_MS);
    };

    ws.onerror = () => {
      if (unmountingRef.current || websocketRef.current === null) return;
      console.error("[investor-chat] WebSocket error - see onclose for details");
    };

    ws.onclose = (event) => {
      const alreadyCleanedUp = websocketRef.current === null;
      websocketRef.current = null;
      clearPingInterval();
      if (unmountingRef.current || alreadyCleanedUp) return;

      if (handleWebSocketCloseAuthError(event.code, event.reason)) return;

      if (event.code !== 1000 && event.code !== 1001) {
        setErrorState(mapWebSocketCloseCodeToMessage(event.code, event.reason));
        return;
      }

      if (status !== "connecting") {
        setStatus("idle");
      }
    };

    ws.onmessage = (rawEvent) => {
      if (unmountingRef.current || websocketRef.current === null) return;
      if (import.meta.env.DEV) {
        const rawData: unknown = rawEvent.data;
        console.info("[investor-chat] Received raw WebSocket message", { data: rawData });
      }
      try {
        const data = JSON.parse(rawEvent.data as string) as TInvestorChatServerMessage;
        if (import.meta.env.DEV) {
          console.info("[investor-chat] Parsed WebSocket message", { data });
        }
        switch (data.type) {
          case "investor_chat_thread_history": {
            if (import.meta.env.DEV) {
              console.info("[investor-chat] Received thread history", {
                threadId: data.thread_id,
                messageCount: data.messages.length,
              });
            }
            // Ignore history for threads that don't match current context
            if (data.thread_id && threadIdRef.current !== data.thread_id) {
              if (import.meta.env.DEV) {
                console.info("[investor-chat] Ignoring thread_history for mismatched thread", {
                  incoming: data.thread_id,
                  current: threadIdRef.current,
                });
              }
              break;
            }
            // Map history messages to TMessage format
            const historyMessages: TMessage[] = Array.isArray(data.messages)
              ? data.messages.map((message) => {
                  // Determine sender based on message_type or sender_id/user_id
                  let sender: "user" | "ai" = "user";
                  if (message.message_type === "ai" || message.message_type === "assistant") {
                    sender = "ai";
                  } else if (message.sender_id || message.user_id) {
                    // If there's a sender_id/user_id, it's likely a user message
                    sender = "user";
                  }

                  return {
                    id: message.id,
                    text: message.content ?? "",
                    sender,
                    createdAt: message.created_at ?? undefined,
                    files:
                      message.files && message.files.length > 0
                        ? message.files.map((file) => ({
                            id: file.id,
                            name: file.name ?? "",
                            url: file.url ?? undefined,
                          }))
                        : undefined,
                  };
                })
              : [];

            // Replace all messages with history
            setMessages(historyMessages);
            setIsThinking(false);
            break;
          }
          case "investor_chat_user_message": {
            if (import.meta.env.DEV) {
              console.info("[investor-chat] Received user message", {
                threadId: data.thread_id,
                messageLength: data.content.length,
              });
            }
            setMessages((prev) => {
              const last = prev[prev.length - 1];
              if (last?.sender === "user" && last.text === data.content) {
                return prev;
              }
              return [
                ...prev,
                {
                  id: `user-${data.timestamp}-${Math.random()}`,
                  text: data.content,
                  sender: "user",
                  createdAt: data.timestamp,
                },
              ];
            });
            break;
          }
          case "investor_chat_ai_status": {
            if (import.meta.env.DEV) {
              console.info("[investor-chat] Received AI status", {
                threadId: data.thread_id,
                status: data.status,
                message: data.message,
              });
            }
            if (data.status === "thinking") {
              setIsThinking(true);
            } else if (data.status === "complete" || data.status === "error") {
              setIsThinking(false);
            }
            break;
          }
          case "investor_chat_ai_response": {
            if (import.meta.env.DEV) {
              console.info("[investor-chat] Received AI response", {
                threadId: data.thread_id,
                messageLength: data.content.length,
              });
            }
            setIsThinking(false);
            setLatestUsage(extractUsageSummary(data));
            setMessages((prev) => [
              ...prev,
              {
                id: `ai-${data.timestamp}-${Math.random()}`,
                text: data.content,
                sender: "ai",
                createdAt: data.timestamp,
              },
            ]);
            break;
          }
          case "error": {
            if (handleWebSocketAuthError(data.message, data.type)) return;
            setIsThinking(false);
            setErrorState(data.message);
            break;
          }
          case "presence_update":
          case "typing_indicator":
          case "pong": {
            // Ignore these message types
            break;
          }
          default: {
            if (import.meta.env.DEV) {
              console.warn("[investor-chat] Ignoring unknown message type", {
                type: (data as { type: string }).type,
              });
            }
          }
        }
      } catch (error: unknown) {
        const rawData: unknown = rawEvent.data;
        console.error("[investor-chat] Failed to parse WebSocket message", {
          error,
          data: rawData,
        });
      }
    };
  }, [investorCompanyId, founderCompanyId, threadId, enabled, setErrorState, clearPingInterval, status]);

  // Fetch thread history on mount when we have a threadId
  useEffect(() => {
    if (!threadId || !enabled || !investorCompanyId) {
      return;
    }

    let isCancelled = false;

    const fetchHistory = async () => {
      if (import.meta.env.DEV) {
        console.info("[investor-chat] Fetching thread history", { threadId });
      }

      const { data, error } = await chatThreadsService.getThreadMessages(threadId);

      if (isCancelled) return;

      if (error) {
        if (import.meta.env.DEV) {
          console.warn("[investor-chat] Failed to fetch thread history", { error, threadId });
        }
        // Don't set error state here - WebSocket connection will handle it
        return;
      }

      if (data?.data?.messages && Array.isArray(data.data.messages)) {
        // Map history messages to TMessage format (same as useChatThreadSession)
        const historyMessages: TMessage[] = (data.data.messages as TChatThreadHistoryMessage[]).map((message) => {
          // Determine sender based on message_type
          const sender: "user" | "ai" = message.message_type === "ai" ? "ai" : "user";

          return {
            id: message.id,
            text: message.content ?? "",
            sender,
            createdAt: message.created_at ?? undefined,
            files:
              message.files && message.files.length > 0
                ? message.files.map((file) => ({
                    id: file.id,
                    name: file.name ?? "",
                    url: file.url ?? undefined,
                  }))
                : undefined,
          };
        });

        if (import.meta.env.DEV) {
          console.info("[investor-chat] Loaded thread history", {
            threadId,
            messageCount: historyMessages.length,
          });
        }

        // Set messages from history
        setMessages(historyMessages);
      }
    };

    void fetchHistory();

    return () => {
      isCancelled = true;
    };
  }, [threadId, enabled, investorCompanyId]);

  // Connect on mount when we have all required params
  useEffect(() => {
    unmountingRef.current = false; // Reset on mount/remount
    if (!investorCompanyId || !founderCompanyId || !threadId || !enabled) {
      if (import.meta.env.DEV) {
        console.info("[investor-chat] Skipping connection due to missing params", {
          investorCompanyId,
          founderCompanyId,
          threadId,
          enabled,
        });
      }
      return;
    }
    const token = tokenStorage.getAccessToken();
    if (!token) {
      if (import.meta.env.DEV) {
        console.info("[investor-chat] Skipping connection due to missing token");
      }
      return;
    }
    if (websocketRef.current?.readyState === WebSocket.OPEN) {
      if (import.meta.env.DEV) {
        console.info("[investor-chat] Skipping connection because WebSocket is already open");
      }
      return;
    }
    if (websocketRef.current?.readyState === WebSocket.CONNECTING) {
      if (import.meta.env.DEV) {
        console.info("[investor-chat] Skipping connection because WebSocket is already connecting");
      }
      return;
    }

    const trimmedInitialPrompt = initialPrompt?.trim() ?? null;
    const hasConsumedInitialPrompt = threadId ? consumedInitialPromptThreadIds.has(threadId) : false;
    const nextPendingPrompt =
      trimmedInitialPrompt && threadId && !hasConsumedInitialPrompt ? trimmedInitialPrompt : null;

    if (import.meta.env.DEV) {
      console.info("[investor-chat] Updating pending initial prompt", {
        threadId,
        hasPrompt: Boolean(trimmedInitialPrompt),
        hasConsumedInitialPrompt,
      });
    }

    if (threadId && nextPendingPrompt) {
      consumedInitialPromptThreadIds.add(threadId);
    }

    pendingMessageRef.current = nextPendingPrompt;
    connect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investorCompanyId, founderCompanyId, threadId, enabled, initialPrompt]);

  const sendMessage = useCallback(
    (content: string) => {
      const trimmed = content.trim();
      if (!trimmed) return;

      const token = tokenStorage.getAccessToken();
      if (!token || !investorCompanyId) {
        setErrorState("Not authenticated");
        return;
      }

      const wsState = websocketRef.current?.readyState;
      if (!websocketRef.current || (wsState !== WebSocket.OPEN && wsState !== WebSocket.CONNECTING)) {
        pendingMessageRef.current = trimmed;
        connect();
        return;
      }
      if (wsState === WebSocket.CONNECTING) {
        pendingMessageRef.current = trimmed;
        if (import.meta.env.DEV) {
          console.info("[investor-chat] Queueing message while WebSocket connects", {
            threadId,
            messageLength: trimmed.length,
          });
        }
        return;
      }

      if (import.meta.env.DEV) {
        console.info("[investor-chat] Sending user message", {
          threadId,
          messageLength: trimmed.length,
        });
      }
      websocketRef.current.send(
        JSON.stringify({
          type: "investor_chat_message",
          section: "deal_pipeline",
          content: trimmed,
          company_id: founderCompanyId,
        })
      );

      setMessages((prev) => [
        ...prev,
        {
          id: `optimistic-${Date.now()}`,
          text: trimmed,
          sender: "user",
          optimistic: true,
        },
      ]);
      setIsThinking(true);
    },
    [investorCompanyId, founderCompanyId, threadId, connect, setErrorState]
  );

  useEffect(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    return () => {
      unmountingRef.current = true;
      closeTimeoutRef.current = setTimeout(() => {
        closeTimeoutRef.current = null;
        if (websocketRef.current) {
          websocketRef.current.close();
          websocketRef.current = null;
        }
        clearPingInterval();
        threadIdRef.current = null;
      }, 150);
    };
  }, [clearPingInterval]);

  return {
    messages,
    status,
    error,
    isThinking,
    latestUsage,
    sendMessage,
  };
}
